/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  Container,
  Grid,
  Divider,
  Typography,
  MenuItem,
  Select,
  Button,
  Box,
} from "@material-ui/core";
import FormControl from '@material-ui/core/FormControl';
import WarningIcon from '@material-ui/icons/WarningRounded';

import ApiInfoBar from '../../shared/ApiInfoBar';
import {
  ApiReducer,
  ApplicationState,
  Subscription,
  SubscriptionList,
  SubscriptionPoliciesReducer,
  SubscriptionsReducer,
  Tier,
  UserReducer,
} from "../../../data/interfaces";
import * as Utils from '../../../data/utils';
import {
  fetchSubscriptions,
  updateSubscription,
  cleanupSubscriptions,
  fetchSubscriptionPoliciesList,
} from "../../../data/operations";
import { useStyles } from './styles';
import OutlineButton from '../../../elements/OutlineButton';
import CustomIcons from '../../../icons/CustomIcons';
import {
  DEFAULT_APPLICATION_LIMIT,
  PATH_SEPARATOR, PRODUCTION, RESOURCE_API, RESOURCE_API_CREDENTIALS, RESOURCE_APPLICATIONS,
} from '../../../data/constants';
import SubscriptionPolicyCard from '../../app/AppSubscriptions/SubscriptionPolicyCard';
interface ApiContractsProps {
    apiReducer: ApiReducer,
    subscriptionsReducer: SubscriptionsReducer,
    subscriptionPoliciesReducer : SubscriptionPoliciesReducer
    userReducer: UserReducer,
    cleanupSubscriptions: () => void,
    fetchSubscriptions: (apiId: string, organizationId: string, 
      type: string, limit?: number | null,) => void,
    fetchSubscriptionPoliciesList: (organizationId: string) => Promise<void>,
    updateSubscription: (subscriptionId: string, subscription: Subscription, organizationId: string) => Promise<void>
}

function ApiContracts(props: ApiContractsProps) {
    const intl = useIntl();
    const { api } = props.apiReducer;
    const { isLoading, subscriptions } = props.subscriptionsReducer;
    const { subscriptionPolicies } = props.subscriptionPoliciesReducer;
    const { selectedOrg } = props.userReducer;
    const { fetchSubscriptions, updateSubscription, cleanupSubscriptions, fetchSubscriptionPoliciesList } = props;
    const classes = useStyles();
    const [radioGroupValue, setRadioGroupValue] = useState('');
    const [selectedSubPolicy, setSelectedSubPolicy] = useState('');
    const [selectedApplication, setSelectedApplication] = useState('');
    const history = useHistory();

    const DEFAULT_CREDENTIALS = "Default Credentials";
    const tiers: Tier[] = api.tiers || [];

    const getSubscriptionByApplication = (subscriptions: SubscriptionList, applicationName: string): Subscription => {
        return subscriptions.list.find((subscription) => {
            if (subscription.applicationInfo) {
                const {name} = subscription.applicationInfo;
                return name === applicationName || name.includes('_internal_') && applicationName.includes(DEFAULT_CREDENTIALS);
            }
            return false;
        }) || {} as Subscription;
    }

    useEffect(() => {
        if (api && Utils.isValidData(api) && selectedOrg.uuid) {
            fetchSubscriptions(api.id, selectedOrg.uuid, "api", DEFAULT_APPLICATION_LIMIT);
            // Set the application limit to 150 . But this is not a proper fix. Need to implement some
            // pagination mechanism for the dropdown
        }
        fetchSubscriptionPoliciesList(selectedOrg.uuid);
        return () => {
            cleanupSubscriptions();
        }
    }, []);

    useEffect(() => {
        if (subscriptions?.list?.length > 0 && (!selectedApplication || !radioGroupValue)) {
            setSelectedApplication(getApplicatonName(subscriptions.list[0].applicationInfo?.name || ''))
            setRadioGroupValue(subscriptions.list[0].throttlingPolicy || '')
        }
    }, [subscriptions])

    useEffect(() => {
      setRadioGroupValue(selectedSubPolicy);
    }, [selectedSubPolicy])

    const handleTryApiClick = () => {
        const selectedOrgHandleName = selectedOrg.handle;
        history.push(PATH_SEPARATOR + selectedOrgHandleName + RESOURCE_API + PATH_SEPARATOR + api.id
            + RESOURCE_API_CREDENTIALS + PATH_SEPARATOR + PRODUCTION);
    }

    const handleGoToApplicationsClick = () => {
      const selectedOrgHandleName = selectedOrg.handle;
      history.push(PATH_SEPARATOR + selectedOrgHandleName + RESOURCE_APPLICATIONS);
    };

    const handleSelectApplication = (event: any) => {
        const { value } = event.target;
        setSelectedApplication(value);
        setRadioGroupValue(getSubscriptionByApplication(subscriptions, value)?.throttlingPolicy || '')
    }

    const getApplicatonName = (name: string): string => {
        if (name.includes('_internal_')) {
            return DEFAULT_CREDENTIALS;
        }
        return name;
    }

  const createSubscription = () => {
    const subscription = getSubscriptionByApplication(subscriptions, selectedApplication);
    const data: Subscription = {
      applicationId: subscription.applicationId,
      apiId: subscription.apiId,
      throttlingPolicy: radioGroupValue,
      requestedThrottlingPolicy: radioGroupValue,
      status: subscription.status,
      subscriptionId: subscription.subscriptionId,
    }
    updateSubscription(subscription.subscriptionId, data, selectedOrg.uuid);
  }

  const subscriptionButtonText = () => {
    const subscription = getSubscriptionByApplication(
      subscriptions,
      selectedApplication
    );
    if (
      subscriptionPolicies?.list?.find(
        (policy) => policy.name === selectedSubPolicy
      )?.attributes?.isApprovalRequired
    ) {
      return subscription.subscriptionId ? (
        <FormattedMessage
          id="application.api.subscriptions.requestUpdateSubscription.button.label"
          defaultMessage="Request Subscription Update"
        />
      ) : (
        <FormattedMessage
          id="application.api.subscriptions.requestSubscription.button.label"
          defaultMessage="Request Subscription"
        />
      );
    } else {
      return subscription.subscriptionId ? (
        <FormattedMessage
          id="application.api.subscriptions.updateSubscription.button.label"
          defaultMessage="Update Subscription"
        />
      ) : (
        <FormattedMessage
          id="application.api.subscriptions.addSubscription.button.label"
          defaultMessage="Add Subscription"
        />
      );
    }
  };

    return (
      <main className={classes.content}>
        <ApiInfoBar />
        <Divider />
        <Container
          className={classes.container}
          classes={{ root: classes.componentPadding }}
        >
          <Grid container spacing={3} direction='column'>
            {!isLoading && subscriptions.count === 0 ? (
              <div>
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  className={classes.formControlWarn}
                >
                  <WarningIcon className={classes.warningIcon} />
                  <Typography>
                    <FormattedMessage
                      id='api.apiContracts.messages.noCredentialMessage'
                      defaultMessage='Generate credentials or subscribe to this API via an application to access contracts.'
                    />
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  className={classes.formControlWarn}
                >
                  <Box display="inline-flex" mr={2}>
                    <OutlineButton
                      onClick={handleTryApiClick}
                      buttonLabel={intl.formatMessage({
                        id: "api.apiContracts.buttons.goToCredentials",
                        defaultMessage: "Go to Credentials",
                      })}
                      startIcon={<CustomIcons icon="try-button-icon" />}
                    />
                  </Box>
                  <Box display="inline-flex">
                    <OutlineButton
                      onClick={handleGoToApplicationsClick}
                      buttonLabel={intl.formatMessage({
                        id: "api.apiContracts.buttons.goToApplications",
                        defaultMessage: "Go to Applications",
                      })}
                      startIcon={<CustomIcons icon="try-button-icon" />}
                    />
                  </Box>
                </Grid>
              </div>
            ) : (
              <>
                <Grid
                  container
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  spacing={4}
                  className={classes.applicationSection}
                >
                  <Grid item>
                    <Typography className={classes.contractsTitle}>
                      <FormattedMessage
                        id='api.apiContracts.labels.subscribed.applicaton'
                        defaultMessage='Subscribed Application'
                      />
                    </Typography>
                  </Grid>
                  <Grid item>
                    <FormControl
                      variant='outlined'
                      data-testid='throttling-tier-formcontrol'
                    >
                      <Select
                        value={selectedApplication}
                        onChange={handleSelectApplication}
                        variant='outlined'
                        className={classes.selectBox}
                        MenuProps={{
                          getContentAnchorEl: null,
                          classes: { paper: classes.dropdownStyle },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                        }}
                        data-testid='throttling-tier-select'
                      >
                        {subscriptions?.list?.length > 0 ? (
                          subscriptions.list.map((subscription) => {
                            return (
                              <MenuItem
                                key={subscription.subscriptionId}
                                value={getApplicatonName(
                                  subscription.applicationInfo?.name || ''
                                )}
                              >
                                {getApplicatonName(
                                  subscription.applicationInfo?.name || ''
                                )}
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem value='No apps found' disabled>
                            <em>
                              <FormattedMessage
                                id='api.apiCredentials.messages.emptyContract'
                                defaultMessage='No Contract Found'
                              />
                            </em>
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid
                  container
                  item
                  direction='column'
                  xs={12}
                  md={12}
                  lg={12}
                  className={classes.formControl}
                >
                  <Grid item>
                    <Typography className={classes.contractsTitle}>
                      <FormattedMessage
                        id='api.apiContracts.labels.availableContracts'
                        defaultMessage='Available contracts'
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
          {subscriptions?.list?.length > 0 && (
            <div>
              <Grid container={true} spacing={1}>
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  className={classes.policyCardGridItem}
                >
                  {tiers.map((tier: Tier) => {
                    const matchedPolicy = subscriptionPolicies?.list?.find(
                      subPolicy => subPolicy.name === tier.tierName
                    )
                    if (matchedPolicy !== undefined) {
                      return (
                        <Grid
                          item
                          xs={3}
                          md={3}
                          lg={3}
                          key={matchedPolicy.name}
                        >
                          <SubscriptionPolicyCard
                            subscriptionPolicy={matchedPolicy}
                            isSelected={
                              radioGroupValue === matchedPolicy.name
                            }
                            handleSubPolicySelection={setSelectedSubPolicy}
                          />
                        </Grid>
                      )
                    }
                  })}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  className={classes.policyCardGridItem}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={createSubscription}
                    className={classes.btnText}
                  >
                    {subscriptionButtonText()}
                  </Button>
                </Grid>
              </Grid>
            </div>
          )}
        </Container>
      </main>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiReducer: state.apiReducer,
        subscriptionsReducer: state.subscriptionsReducer,
        userReducer: state.userReducer,
        subscriptionPoliciesReducer: state.subscriptionPoliciesReducer
    }
}

const mapDispatchToProps = {
    fetchSubscriptions: fetchSubscriptions,
    updateSubscription: updateSubscription,
    cleanupSubscriptions: cleanupSubscriptions,
    fetchSubscriptionPoliciesList: fetchSubscriptionPoliciesList
}

export default connect(mapStateToProps, mapDispatchToProps)(ApiContracts);
